import { Theme } from '@mui/material';
import { makeStyles } from '@mui/styles';
import AutoAwesomeOutlinedIcon from '@mui/icons-material/AutoAwesomeOutlined';
import React, { useEffect, useRef, useState } from 'react';
import Colors from '../../utils/color';
import { HITL3_FONT_SIZE } from '../../utils/hitl3Typography';
import { HITL3_PADDING } from '../../utils/hitl3Spacing';
import getStatusBarColor from '../../utils/getStatusBarColor';
import ConsoleCard from '../MainPageLayout/ConsoleCard';
import MenuBoardTimeTile from './MenuBoardTimeTile';
import ResponseTimeTile from './ResponseTimeTile';
import TurnTile from './TurnTile';
import SentimentTile from './SentimentTile';
import PulseSignals from './PulseSignals';
import PulseAdvise from './PulseAdvise';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import {
  selectAIStatus,
  selectIsAIEscalation,
  selectAIEscalationReason,
  selectRequiresTakeover,
  selectIsAIEscalatedOnCarEnter,
  selectIsAIAutoMode,
} from '../../redux/features/ai/ai.selector';
import { resetAIControlState } from '../../redux/features/ai/ai.slice';
import { selectFeatureFlagUnresolvedItems } from '../../redux/features/config/config.selector';
import { selectIsCarPresent } from '../../selectors/message';
import useIntervention from '../../hooks/intervention/useIntervention.hooks';

/**
 * CUSTOMER PULSE (left pane, top card).
 *
 * A collapsible card that gives the human a 2-second read of the live order's AI
 * status. The header (icon + title + collapse chevron) sits inside the white card
 * (matching Action Center); a full-bleed status band pinned at the card's bottom
 * edge encodes the LIVE AI status — green/yellow/red soft tints, plus a prominent
 * vivid blink on takeover.
 *
 * Under the header the card is a compact strip: the Menu Board Time + Response
 * time meters, the live signal + reminder chips (PulseSignals), and the folded-in
 * AI co-pilot ask (PulseAdvise) — with the AI status band pinned at the bottom.
 */

const useStyles = makeStyles((theme: Theme) => ({
  // AI status band (folded-in AIStatusBar): a full-bleed strip pinned at the
  // card's bottom edge (marginTop:auto pushes it down in the flex column).
  // Background encodes the LIVE AI status and is applied inline via the shared
  // getStatusBarColor() so it matches the legacy AIStatusBar exactly (vivid
  // green/yellow/red), plus a prominent vivid blink for a takeover
  // (bandTakeover). The card padding is 0, so the band already spans edge-to-edge
  // (no negative margins needed); the shared 8px inset pads its text on all sides.
  statusBand: {
    flexShrink: 0,
    marginTop: 'auto',
    padding: HITL3_PADDING,
    // Text styled like the "Continue" intent pill: medium weight, shared hitl3 size.
    fontSize: HITL3_FONT_SIZE,
    fontWeight: 500,
    lineHeight: 1.3,
    // Left-aligned text, vertically centered within the band's available height.
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    overflow: 'hidden',
  },
  // One line: truncate with an ellipsis (no wrap, no h-scroll). minWidth:0 lets
  // this flex child shrink so the ellipsis engages; the full message shows on
  // hover via the title attribute on the band.
  statusText: {
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  // Takeover: a prominent vivid red throb (light↔dark) with white text — a
  // "must not miss" alert, so intentionally NOT a soft tint. Static $keyframes
  // reference (JSS resolves the scoped name only in a static rule).
  '@keyframes statusTakeoverPulse': {
    '0%, 100%': { backgroundColor: '#ff5c5c' },
    '50%': { backgroundColor: '#990000' },
  },
  bandTakeover: {
    color: Colors.white,
    animation: '$statusTakeoverPulse 1s ease-in-out infinite',
  },
  // HITL 3.0 metric tiles row, laid out side-by-side under the header. Each tile
  // self-gates to null, so the row can be empty until one becomes active.
  // flex-start (not center) tops-align the tiles so every tile's value + label
  // sit on the same line — tiles with a progress bar (Menu Board Time, Response
  // time) are taller than those without (Turn, Sentiment), and centering would
  // otherwise push the shorter tiles' text down.
  metricRow: {
    marginTop: HITL3_PADDING,
    paddingLeft: HITL3_PADDING,
    paddingRight: HITL3_PADDING,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2),
    flexWrap: 'wrap',
  },
}));

interface CustomerPulseProps {
  // Optional controlled collapse so the parent can drive the resizable panel
  // (collapsing frees its space for the transcript). Falls back to internal
  // state (default open) when used standalone.
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const CustomerPulse = ({
  open: openProp,
  onOpenChange,
}: CustomerPulseProps = {}) => {
  const classes = useStyles();
  const [openState, setOpenState] = useState(true);
  const open = openProp ?? openState;
  const toggleOpen = () => {
    const next = !open;
    if (openProp === undefined) setOpenState(next);
    onOpenChange?.(next);
  };

  // AI status band — wired to the live AI status. Precedence:
  // takeover-blink > agent-intercepted(red) > escalation(red / car-enter orange)
  // > AI status color (green/yellow/red).
  const aiStatus = useAppSelector(selectAIStatus);
  const isAIEscalation = useAppSelector(selectIsAIEscalation);
  const aiEscalationReason = useAppSelector(selectAIEscalationReason);
  const requiresTakeover = useAppSelector(selectRequiresTakeover);
  const isAIEscalatedOnCarEnter = useAppSelector(selectIsAIEscalatedOnCarEnter);
  const isAIAutoMode = useAppSelector(selectIsAIAutoMode);
  const unresolvedItemsEnabled = useAppSelector(
    selectFeatureFlagUnresolvedItems
  );
  const isCarPresent = useAppSelector(selectIsCarPresent);
  const { isStaffInterventionHappening } = useIntervention();
  const dispatch = useAppDispatch();

  // Car has left after an intercepted order — the session is over, so clear the
  // lingering AI control state. Finish Order intentionally SKIPS the reset while
  // the car is present (so the "Agent Intercept" status stays for the rest of
  // the visit — see OrderActions), which means the reset must land here, on car
  // exit. Without this, isAIAutoMode/aiStatus stay stuck at their intercept
  // values (no car_exit reset exists in ai.slice, and useSessionStatus only
  // re-arms on the NEXT car's session start), so the band would show a stale
  // "Agent Intercept" between car exit and the next car.
  //
  // EDGE-triggered on the car-leaving transition (present → absent), NOT the
  // steady "no car" state. A level-triggered `!isCarPresent && !isAIAutoMode`
  // would re-fire whenever an agent's own action flips isAIAutoMode false while
  // idle with no car, forcing it back to auto and re-arming the take-over
  // confirmation popup on every keystroke (agent couldn't type). By only acting
  // on the falling edge of isCarPresent, this runs once as the car departs.
  // hitl_3_0-only — this component mounts solely inside MainPageLayout.
  const prevCarPresentRef = useRef(isCarPresent);
  useEffect(() => {
    const carJustLeft = prevCarPresentRef.current && !isCarPresent;
    prevCarPresentRef.current = isCarPresent;
    if (carJustLeft && !isAIAutoMode) {
      dispatch(resetAIControlState());
    }
  }, [isCarPresent, isAIAutoMode, dispatch]);

  // A takeover was requested (the AI halted). While the AI is still nominally in
  // control (isAIAutoMode) the band BLINKS — a "take over now" alert. The moment
  // the agent takes any manual action, isAIAutoMode flips to false: the takeover
  // has been intercepted, so the blink resolves to a static "Agent interception"
  // in the vivid escalation red. requiresTakeover is sticky, so this holds
  // until the session resets for the next order.
  const takeoverActive = unresolvedItemsEnabled && requiresTakeover;
  const showPulse = takeoverActive && isAIAutoMode;
  const agentIntercepted = takeoverActive && !isAIAutoMode;

  // Staff intervention outranks every AI status: a human now owns the
  // conversation, so the band reads "Staff Intervention" (static, no blink)
  // regardless of the underlying AI/takeover state.
  const statusMessage = isStaffInterventionHappening
    ? 'Staff Intervention'
    : agentIntercepted
    ? 'Agent Intercept'
    : isAIEscalation
    ? aiEscalationReason || aiStatus.message
    : aiStatus.message;

  // The takeover blink is suppressed during staff intervention — the static
  // "hand-off" red is the resolved, unambiguous state.
  const showTakeoverBlink = showPulse && !isStaffInterventionHappening;

  // Background/text match the legacy AIStatusBar exactly: showPulse drives the
  // vivid takeover blink via the bandTakeover class; every other state derives
  // its background from the shared getStatusBarColor(). agentIntercepted is a
  // HITL 3.0-only static state (the legacy bar unmounts on intercept) — render
  // it in the same vivid escalation red. Text is white on the saturated
  // escalation/intercept fills (as in AIStatusBar) and inherit (dark) otherwise.
  const bandBackground = isStaffInterventionHappening
    ? Colors.torchRed
    : agentIntercepted
    ? Colors.red
    : getStatusBarColor(
        isAIEscalation,
        isAIEscalatedOnCarEnter,
        aiStatus.status
      );
  const bandTextColor =
    isStaffInterventionHappening || isAIEscalation || agentIntercepted
      ? Colors.white
      : 'inherit';

  return (
    <ConsoleCard
      icon={<AutoAwesomeOutlinedIcon />}
      title="AI Copilot"
      open={open}
      onToggle={toggleOpen}
      testId="customer-pulse"
      footer={
        // AI status band ALWAYS renders — the live status (and the takeover
        // blink) must stay visible even when the card is collapsed to a strip.
        // marginTop:auto pins it to the card's bottom edge in both states.
        <div
          className={`${classes.statusBand}${
            showTakeoverBlink ? ` ${classes.bandTakeover}` : ''
          }`}
          style={
            showTakeoverBlink
              ? undefined
              : { backgroundColor: bandBackground, color: bandTextColor }
          }
          title={statusMessage}
        >
          <span className={classes.statusText}>{statusMessage}</span>
        </div>
      }
    >
      {/* HITL 3.0 Pulse strip: the metric tiles (side by side) → the signal +
          reminder chips → the folded-in AI ask. The whole console is already
          gated on hitl_3_0 at the mount site (Body), so no local flag branch is
          needed. Each tile self-gates:
            • Menu Board Time — while a session is active; the car's dwell gauge.
            • Turn — active session + AI enabled; the ai_session_state turn count
              (no progress bar).
            • Sentiment — active session + AI enabled; the ai_session_state
              sentiment (colour + face, muted "—" default).
            • Response time — active session + (agent intercepted OR pure HITL);
              the per-turn reply SLA.
          PulseSignals / PulseAdvise self-gate to null when there's nothing to
          surface.

          During staff intervention the whole body is blanked — the metrics,
          signals and AI ask all describe an AI-run order that no longer applies
          once a human takes over. Only the AI status band (footer) stays, now
          reading "Staff Intervention". */}
      {!isStaffInterventionHappening && (
        <>
          <div className={classes.metricRow}>
            <MenuBoardTimeTile />
            <TurnTile />
            <SentimentTile />
            <ResponseTimeTile />
          </div>
          <PulseSignals />
          <PulseAdvise />
        </>
      )}
    </ConsoleCard>
  );
};

export default CustomerPulse;
